import * as React from 'react';
import { useEffect } from 'react';
import { CreateAppPayload, getSteps, GitProvider, VersionControl } from './CreateAppPage';
import { FormikContext, useFormik } from 'formik';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { styled } from '../../../theme';
import { CheckboxLabelWidget } from '../../../widgets/checkbox/CheckboxLabelWidget';
import { InputWrapperWidget } from '../../../widgets/inputs';
import * as accounts from '@journeyapps-platform/types-accounts-hub';
import { SourceType } from '@journeyapps-platform/types-source-service';
import { GithubSourceControlSubStepWidget } from './github/GithubSourceControlSubStepWidget';
import { useSystem } from '../../../hooks/useSystem';
import { CheckboxType } from '../../../widgets/checkbox/CheckboxWidget';
import { Keys, useKeyPress } from '../../../hooks/useKeyPress';
import { ButtonType } from '../../../widgets/buttons/ButtonWidget';
import { BreadCrumbWidget } from '../../../widgets/header/BreadCrumbWidget';
import { OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { JourneyNavigationHandler, Routes } from '../../../routes';
import { SelectWidget } from '../../../widgets/select/SelectWidget';
import * as _ from 'lodash';
import { AzureReposSourceControlSubStepWidget } from './azure/AzureReposSourceControlSubStepWidget';
import { v4 } from 'uuid';
import { BetaPillWidget } from '../../../widgets/BetaPillWidget';

export interface GitProviderWidgetProps {
  validProviders: SourceType[];
  provider: SourceType;
  providerChanged: (provider: SourceType) => any;
}

export const GitProviderWidget: React.FC<GitProviderWidgetProps> = (props) => {
  return (
    <SelectWidget
      placeholder={'Select Git Provider'}
      onChange={(value: any) => {
        props.providerChanged(value.value);
      }}
      value={{
        value: props.provider,
        label: GitProvider[props.provider]
      }}
      getDecorations={(v) => {
        if (v.value === SourceType.AzureDevops) {
          return <BetaPillWidget />;
        }
        return null;
      }}
      options={_.chain(props.validProviders)
        .orderBy((type) => GitProvider[type as SourceType])
        .map((type) => {
          return {
            value: type,
            label: GitProvider[type as SourceType]
          };
        })
        .value()}
    />
  );
};

export interface CreateAppStep2WidgetProps {
  payload: Partial<CreateAppPayload>;
  org: OrganizationModel;
  updatePayload: (payload: Partial<CreateAppPayload>, next?: boolean) => any;
  back: () => any;
  onCancel: () => any;
  handler: JourneyNavigationHandler<Routes.CREATE_APP>;
}

type CreateAppStep2Errors = {
  control?: string;
  provider?: string;
};

export const CreateAppStep2Widget: React.FC<CreateAppStep2WidgetProps> = (props) => {
  const system = useSystem();
  const formik = useFormik<{
    control?: accounts.VCS_MODE;
    provider?: SourceType | null;
  }>({
    initialValues: {
      control: props.payload.vcs_mode,
      provider: (props.payload.source?.type as SourceType) || SourceType.Internal
    },
    onSubmit: (event) => {
      props.updatePayload(
        {
          ...props.payload,
          vcs_mode: event.control,
          source: props.payload.source
        },
        true
      );
    },
    validateOnBlur: false,
    validateOnChange: false,
    validate: (values) => {
      const errors: Partial<CreateAppStep2Errors> = {};
      if (!values.control) {
        errors['control'] = 'Please select an option';
        return errors;
      }
      if (values.control === accounts.VCS_MODE.ADVANCED && !values.provider) {
        errors['provider'] = 'Please select an option';
      }
      return errors;
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  useEffect(() => {
    if (formik.values.provider === SourceType.Internal) {
      props.updatePayload({
        source: {
          type: SourceType.Internal,
          properties: {
            id: v4()
          }
        }
      });
    }
  }, [formik.values.provider]);

  const getContent = (): FormLayoutSection => {
    const elements = [
      <S.Container>
        <S.Paragraph>
          How would you like to manage changes to <b>{props.payload.name}</b>?
        </S.Paragraph>
        <InputWrapperWidget error={!formik.values.control && formik.errors.control}>
          <S.Box>
            <S.Checkbox
              type={CheckboxType.CIRCLE}
              label={
                <S.LabelSubtext>
                  <S.Label>{VersionControl[accounts.VCS_MODE.BASIC]}</S.Label>
                  <S.SubText>A simple workflow with basic restore points</S.SubText>
                </S.LabelSubtext>
              }
              checked={formik.values.control === accounts.VCS_MODE.BASIC}
              onChange={() => {
                formik.setValues({ ...formik.values, control: accounts.VCS_MODE.BASIC, provider: null });
              }}
            />
          </S.Box>
          <S.Checkbox
            type={CheckboxType.CIRCLE}
            label={
              <S.LabelSubtext>
                <S.Label>{VersionControl[accounts.VCS_MODE.ADVANCED]}</S.Label>
                <S.SubText>A git-based workflow with commits, branching and merging</S.SubText>
              </S.LabelSubtext>
            }
            checked={formik.values.control === accounts.VCS_MODE.ADVANCED}
            onChange={() => {
              formik.setFieldValue('control', accounts.VCS_MODE.ADVANCED);
            }}
          />
        </InputWrapperWidget>
      </S.Container>
    ];

    elements.push(
      <S.Container>
        <S.Paragraph>
          Which Git provider would you like to use for <b>{props.payload.name}</b>?
        </S.Paragraph>
        <InputWrapperWidget error={!formik.values.provider && formik.errors.provider}>
          <GitProviderWidget
            validProviders={_.values(SourceType)}
            provider={formik.values.provider!}
            providerChanged={(provider) => {
              formik.setFieldValue('provider', provider);
            }}
          />
        </InputWrapperWidget>
      </S.Container>
    );

    const shared = {
      createIntegration: () => {
        props.handler.navigate(Routes.CREATE_INTEGRATION, props.handler.params);
      },
      org: props.org,
      name: props.payload.name!,
      payloadChanged: (payload: Partial<CreateAppPayload>) => {
        props.updatePayload({ ...props.payload, ...payload });
      },
      system: system
    };

    if (formik.values.provider === SourceType.GitHub) {
      elements.push(
        <InputWrapperWidget>
          <GithubSourceControlSubStepWidget
            {...shared}
            payload={{
              create_repo: props.payload.create_repo,
              source: {
                type: SourceType.GitHub,
                properties: {
                  owner: (props.payload.source?.properties as any)?.owner || '',
                  repo: (props.payload.source?.properties as any)?.repo || ''
                }
              }
            }}
          />
        </InputWrapperWidget>
      );
    } else if (formik.values.provider === SourceType.AzureDevops) {
      elements.push(
        <InputWrapperWidget>
          <AzureReposSourceControlSubStepWidget
            {...shared}
            payload={{
              create_repo: props.payload.create_repo,
              source: {
                type: SourceType.AzureDevops,
                properties: {
                  organization: (props.payload.source?.properties as any)?.organization || '',
                  project: (props.payload.source?.properties as any)?.project || '',
                  repo: (props.payload.source?.properties as any)?.repo || ''
                }
              }
            }}
          />
        </InputWrapperWidget>
      );
    }

    return {
      key: 'version_control',
      elements: elements
    };
  };

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              type: ButtonType.TERTIARY,
              action: () => {
                props.onCancel();
              }
            },
            {
              label: 'Back',
              type: ButtonType.SECONDARY,
              action: () => {
                props.back();
              }
            },
            {
              label: 'Next',
              action: () => {
                formik.submitForm();
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget noLogo={true} primaryLabel={'Create new project'} orientation={CenterFormOrientation.ROW}>
        <BreadCrumbWidget steps={getSteps(props.payload.type!)} currentStep={1} />
        <FormLayoutWidget sections={[getContent(), getButtons()]} />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

export namespace S {
  export const Container = styled.div``;

  export const Label = styled.div`
    font-size: 16px;
  `;

  export const SubText = styled.div`
    font-size: 12px;
    color: ${(p) => p.theme.text.secondary};
  `;

  export const LabelSubtext = styled.div``;

  export const Paragraph = styled(ParagraphWidget)`
    text-align: left;
    margin-bottom: 16px;
  `;

  export const Box = styled.div`
    padding-bottom: 8px;
  `;

  export const Checkbox = styled(CheckboxLabelWidget)``;
}
